import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Label from 'components/Input/Label';
import LineItem from './LineItem';
import { Link, Redirect } from 'react-router-dom';
import CheckInDetailsContainer from 'containers/CheckInDetailsContainer';
import { getBookingEnginePath } from 'utils/routeUtils';
import minBy from 'lodash/minBy';
import find from 'lodash/find';
import { PROPERTIES, AAA, PROPERTY_TAX } from 'utils/constants';

const H5 = styled.h5`
  margin-bottom: 1em;
`;

const RoomInfo = styled.p`
  a {
    margin-left: 0.5em;
  }
`;

// Find the discounts amount
const calculateDiscounts = (subTotal, discounts) => {
  let discountsTotal = 0;
  discounts.forEach(discount => {
    if (discount && discount.label === AAA) {
      discountsTotal = subTotal / 10;
    }
  });

  return discountsTotal;
};

function ReservationDetails({
  className,
  numberOfNights,
  selectedRoom,
  property,
  discounts
}) {
  if (!numberOfNights || !selectedRoom) {
    return <Redirect to={getBookingEnginePath()} />;
  }

  // Find the lowest roomRate.
  const minRoomRate = minBy(
    selectedRoom.roomRates,
    roomRate => roomRate.total.amount
  );

  // Find the rack rates, if available.
  const rackRoomRate = find(
    selectedRoom.roomRates,
    roomRate => roomRate.ratePlanCode === '10_FIT' && roomRate.total.amount > 0
  );
  const isDiscounted =
    rackRoomRate &&
    Math.round(rackRoomRate.total.amount / 100) !==
      Math.round(minRoomRate.total.amount / 100);
  const originalSubtotal = isDiscounted
    ? rackRoomRate.expectedCharges.totalRoomAndRatePackages
    : null;
  const originalTax = isDiscounted
    ? rackRoomRate.expectedCharges.totalTaxesAndFees
    : null;
  const hasDiscounts = discounts && discounts.length > 0;

  const tax = minRoomRate.expectedCharges.totalTaxesAndFees;
  const subTotal = Math.max(0, minRoomRate.total.amount - tax);

  // Calculate the discount amount and the new tax value
  const discountsTotal =
    (hasDiscounts && calculateDiscounts(subTotal, discounts)) || 0;
  const taxWithDiscount = hasDiscounts
    ? (subTotal - discountsTotal) * PROPERTY_TAX[property]
    : tax;

  const roomItem = {
    quantity: 1,
    name: selectedRoom.roomType.roomTypeDescription,
    price: subTotal,
    originalPrice: originalSubtotal,
    details: [
      selectedRoom.smoking ? 'Smoking' : 'Non-Smoking',
      selectedRoom.accessible ? 'Accessible' : 'Non-Accessible'
    ]
  };

  const taxItem = {
    name: 'Tax',
    price: taxWithDiscount,
    originalPrice: originalTax
  };

  const subTotalItem = {
    name: 'Subtotal',
    price: subTotal,
    originalPrice: originalSubtotal
  };

  const discountItem = {
    name: 'Discount',
    price: -discountsTotal
  };

  const totalItem = {
    name: 'TOTAL',
    price: subTotal + taxWithDiscount - discountsTotal,
    originalPrice: isDiscounted ? originalSubtotal + originalTax : null
  };

  return (
    <div className={className}>
      <H5>YOUR WIND CREEK {PROPERTIES[property].toUpperCase()} RESERVATION</H5>
      <CheckInDetailsContainer />
      <hr />
      <RoomInfo>
        <Label>Room Information</Label>
        <Link
          to={{
            pathname: getBookingEnginePath('choose-accommodation'),
            state: { transitionDirection: 'back' }
          }}
          aria-label="Choose a different room"
        >
          Edit Room
        </Link>
      </RoomInfo>
      <LineItem item={roomItem} />
      <LineItem item={subTotalItem} />
      <hr />
      {hasDiscounts && (
        <React.Fragment>
          <LineItem item={discountItem} />
          <hr />
        </React.Fragment>
      )}
      <LineItem item={taxItem} isTax={true} />
      <hr />
      <LineItem item={totalItem} isTotal={true} />
    </div>
  );
}

ReservationDetails.propTypes = {
  className: PropTypes.string,
  numberOfNights: PropTypes.number,
  selectedRoom: PropTypes.object,
  property: PropTypes.string,
  discounts: PropTypes.arrayOf(PropTypes.string)
};

export default ReservationDetails;
